// The page anatomy of Console D (styles/components.css): a head (crumb, title, mono meta,
// actions), then sections that open with a 1 px ink rule and a short title.
import { createContext, type ReactNode, useContext, useId } from 'react'

/**
 * What an app puts under every page head (APS-228): the console's shell gives the bar's filter
 * chips, shown on a window too narrow for the bar. Nothing by default.
 */
export const PageHeadRow = createContext<ReactNode>(null)

/**
 * The page head. The title is the view's name, or the object's on a detail page (`mono`: an
 * id, a symbol, `file:line`). `say` is the sentence headline, for Home only.
 */
export function PageHead({
  crumb,
  title,
  variant,
  meta,
  actions,
}: {
  crumb?: ReactNode
  title: ReactNode
  variant?: 'mono' | 'say' | undefined
  meta?: ReactNode
  actions?: ReactNode
}) {
  const row = useContext(PageHeadRow)
  return (
    <>
      <div className="pg-head">
        <div>
          {crumb ? <p className="pg-crumb">{crumb}</p> : null}
          <h1 className={variant ? `pg-title ${variant}` : 'pg-title'}>{title}</h1>
          {meta ? <p className="pg-meta">{meta}</p> : null}
        </div>
        {actions ? <div className="pg-acts">{actions}</div> : null}
      </div>
      {row}
    </>
  )
}

/** A section's head alone: the ink rule, the title, a mono meta and the right side. */
export function SectionHead({
  id,
  title,
  meta,
  right,
  minor,
}: {
  id?: string | undefined
  title: ReactNode
  meta?: ReactNode
  right?: ReactNode
  minor?: boolean | undefined
}) {
  return (
    <div className={minor ? 'sec-h minor' : 'sec-h'}>
      <h2 id={id}>{title}</h2>
      {meta ? <span className="m">{meta}</span> : null}
      {right ? <span className="r">{right}</span> : null}
    </div>
  )
}

/** A section: its head, then its content. `minor` draws the rule in the hairline colour. */
export function Section({
  title,
  meta,
  right,
  minor,
  className,
  children,
}: {
  title: ReactNode
  meta?: ReactNode
  right?: ReactNode
  minor?: boolean
  className?: string
  children?: ReactNode
}) {
  const id = useId()
  return (
    <section className={className ? `sec ${className}` : 'sec'} aria-labelledby={id}>
      <SectionHead id={id} title={title} meta={meta} right={right} minor={minor} />
      {children}
    </section>
  )
}
